import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n, LANGS, withLang } from "../i18n";

export const SITE = "Rewardlance";
export const SITE_URL = "https://rewardlance.com";
const DEFAULT_DESC = "Earn real money completing usability tests, surveys, product tests and bug reports for verified businesses. Every reward is reviewed and recorded before payout.";

function meta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function link(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const IS_LIVE = /(^|\.)rewardlance\.com$/.test(window.location.hostname);

function alternates(page) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((e) => e.remove());
  if (!page) return;
  [...LANGS.map(([c]) => [c, c]), ["x-default", "en"]].forEach(([h, c]) => {
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = h;
    el.href = `${SITE_URL}${withLang(c, page)}`;
    document.head.appendChild(el);
  });
}

export function Seo({ title: tt, description: dd, noindex = false, jsonLd, page }) {
  const { pathname } = useLocation();
  const { t, lang } = useI18n();
  const s = page ? t.seo?.[page] || {} : {};
  const title = page ? s.title : tt;
  const description = (page ? s.description : dd) || DEFAULT_DESC;
  useEffect(() => {
    const full = title ? `${title} | RewardLance` : t.homeTitle;
    alternates(page);
    meta("property", "og:locale", lang);
    const url = `${SITE_URL}${pathname}`;
    document.title = full;
    meta("name", "description", description);
    meta("name", "robots", noindex || !IS_LIVE ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large");
    meta("property", "og:title", full);
    meta("property", "og:description", description);
    meta("property", "og:url", url);
    meta("property", "og:type", "website");
    meta("property", "og:site_name", SITE);
    meta("property", "og:image", `${SITE_URL}/og-image.png`);
    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:image", `${SITE_URL}/og-image.png`);
    meta("name", "twitter:title", full);
    meta("name", "twitter:description", description);
    link("canonical", url);
    const old = document.getElementById("page-jsonld");
    if (old) old.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "page-jsonld";
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, noindex, jsonLd, pathname, page, lang, t.homeTitle]);
  return null;
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export const PAGE_SEO = {
  "/": { title: null, description: DEFAULT_DESC },
  "/how-it-works": { title: "How It Works: Earn Rewards or Launch a Campaign", description: "See exactly how Rewardlance works for contributors and businesses: pick a task, submit evidence, get approved and withdraw, or fund a campaign and collect validated results." },
  "/categories": { title: "Task Categories: Usability Tests, Surveys, Bug Reports & More", description: "Browse paid task categories on Rewardlance, including website usability testing, market research surveys, app testing, bug reporting, data validation and content feedback." },
  "/businesses": { title: "For Businesses: Usability Testing & Market Research Panels", description: "Launch usability tests, research surveys and QA campaigns with verified contributors. Fraud checks, manual review and downloadable reports. Request a tailored proposal." },
  "/pricing": { title: "Business Solutions: User Testing, Research & Feedback | Request a Proposal", description: "Get genuine feedback, usability testing, QA and market research from verified participants. Tell us your goals and a Rewardlance representative will prepare a tailored proposal." },
  "/faq": { title: "FAQ: Payments, Withdrawals, Tasks & Account Questions", description: "Answers to common questions about earning on Rewardlance: eligibility, how reviews work, minimum withdrawal, payout methods, rejected submissions and account security." },
  "/about": { title: "About Us: Honest Work, Accurate Rewards", description: "Rewardlance connects businesses that need real feedback with people who want flexible, fairly paid online work, built on transparent accounting and strict quality rules." },
  "/contact": { title: "Contact Us: Support for Contributors & Businesses", description: "Get in touch with the Rewardlance team about your account, payouts, campaigns, partnerships or compliance. We reply within one business day." },
  "/terms": { title: "Terms and Conditions", description: "The terms that govern your use of Rewardlance as a contributor or business, including rewards, withdrawals, prohibited activities and account rules." },
  "/privacy": { title: "Privacy Policy", description: "How Rewardlance collects, uses, protects and retains personal data, and how you can access, correct or delete your information." },
};
